import { Alert, App as AntApp, Button, DatePicker, Drawer, Form, Grid, Input, Radio, Select, Space } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";
import { defaultYear, useAcademicYears } from "../app/classes.ts";
import { SCHOOL_TIME_ZONE } from "../app/time.ts";
import {
  announcementKeys,
  audiences,
  grades,
  MAX_CLASSES,
  TEXT_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  useAnnouncementPreview,
  type AnnouncementAudience,
} from "../app/announcements.ts";
import { AnnouncementRecipientsPreview } from "./AnnouncementRecipientsPreview.tsx";

interface ComposerForm {
  audience: AnnouncementAudience;
  grades?: number[];
  classIds?: string[];
  title: string;
  text: string;
  when: "now" | "later";
  scheduledAt?: Dayjs;
}

interface Props {
  open: boolean;
  onClose(): void;
}

/** The time picked in the form, read as school time (Tashkent), as an ISO instant for the API. */
function scheduledInstant(value: Dayjs | undefined): string | undefined {
  return value ? dayjs.tz(value.format("YYYY-MM-DD HH:mm"), SCHOOL_TIME_ZONE).toISOString() : undefined;
}

/**
 * Writes a school announcement (bd-3qg.19): audience, title and text, now or at a set time. The recipient count is
 * previewed from the server before sending and shown again in the confirmation. Requires announcements.send.
 */
export function AnnouncementComposerDrawer({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { modal, message } = AntApp.useApp();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ComposerForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  // One key per opened composer, so a retried "send" never creates a second announcement.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const audience = Form.useWatch("audience", form);
  const chosenGrades = Form.useWatch("grades", form);
  const chosenClasses = Form.useWatch("classIds", form);
  const when = Form.useWatch("when", form);
  const scheduledAt = Form.useWatch("scheduledAt", form);

  const years = useAcademicYears();
  const year = defaultYear(years.data);
  const classes = useQuery({
    queryKey: ["academic", "classes", "list", year?.id],
    enabled: open && audience === "Classes" && year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const target = {
    audience,
    grades: audience === "Grades" ? (chosenGrades ?? []) : [],
    classIds: audience === "Classes" ? (chosenClasses ?? []) : [],
    scheduledAt: when === "later" ? scheduledInstant(scheduledAt) : undefined,
  };
  const preview = useAnnouncementPreview(target, open);

  const mutation = useMutation({
    mutationFn: async (values: ComposerForm) => {
      const { data, error, response } = await api.POST("/announcements", {
        body: {
          idempotencyKey,
          audience: values.audience,
          grades: target.grades,
          classIds: target.classIds,
          title: values.title.trim(),
          text: values.text.trim(),
          scheduledAt: values.when === "later" ? (scheduledInstant(values.scheduledAt) ?? null) : null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      void message.success(saved.status === "Scheduled" ? t("announcements.composer.scheduled") : t("announcements.composer.sent", { count: Number(saved.queuedCount) }));
      onClose();
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  function confirmAndSend(values: ComposerForm, linkedChats: number | undefined) {
    modal.confirm({
      title: values.when === "later" ? t("announcements.composer.confirmScheduleTitle") : t("announcements.composer.confirmSendTitle"),
      content: linkedChats === undefined ? t("announcements.composer.confirmUnknown") : t("announcements.composer.confirmCount", { count: linkedChats }),
      okText: values.when === "later" ? t("announcements.composer.schedule") : t("announcements.composer.send"),
      cancelText: t("common.cancel"),
      onOk: () => mutation.mutate(values),
    });
  }

  return (
    <Drawer
      title={t("announcements.composer.title")}
      open={open}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {when === "later" ? t("announcements.composer.schedule") : t("announcements.composer.send")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<ComposerForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ audience: "AllParents", when: "now" }}
        onFinish={(values) => confirmAndSend(values, preview.data === undefined ? undefined : Number(preview.data.linkedChats))}
      >
        <Form.Item name="audience" label={t("announcements.composer.audience")}>
          <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 8 }} options={audiences.map((a) => ({ value: a, label: t(`announcements.audiences.${a}`) }))} />
        </Form.Item>
        {audience === "Grades" && (
          <Form.Item name="grades" label={t("announcements.composer.grades")} rules={[{ required: true, message: t("announcements.composer.gradesRequired") }]}>
            <Select mode="multiple" allowClear options={grades.map((g) => ({ value: g, label: t("announcements.gradeLabel", { grade: g }) }))} />
          </Form.Item>
        )}
        {audience === "Classes" && (
          <Form.Item
            name="classIds"
            label={year ? t("announcements.composer.classesOfYear", { year: year.label }) : t("announcements.composer.classes")}
            rules={[{ required: true, message: t("announcements.composer.classesRequired") }]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch={{ optionFilterProp: "label" }}
              maxCount={MAX_CLASSES}
              loading={classes.isPending && classes.fetchStatus !== "idle"}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
        )}
        <AnnouncementRecipientsPreview query={preview} />
        <Form.Item name="title" label={t("announcements.composer.titleLabel")} rules={[{ required: true, whitespace: true, message: t("announcements.composer.titleRequired") }]}>
          <Input maxLength={TITLE_MAX_LENGTH} showCount />
        </Form.Item>
        <Form.Item
          name="text"
          label={t("announcements.composer.text")}
          extra={t("announcements.composer.textHint")}
          rules={[{ required: true, whitespace: true, message: t("announcements.composer.textRequired") }]}
        >
          {/* The character count sits below the box; the margin keeps it clear of the hint. */}
          <Input.TextArea maxLength={TEXT_MAX_LENGTH} showCount autoSize={{ minRows: 5, maxRows: 14 }} style={{ marginBottom: 24 }} />
        </Form.Item>
        <Form.Item name="when" label={t("announcements.composer.when")}>
          <Radio.Group
            options={[
              { value: "now", label: t("announcements.composer.now") },
              { value: "later", label: t("announcements.composer.later") },
            ]}
          />
        </Form.Item>
        {when === "later" && (
          <Form.Item
            name="scheduledAt"
            label={t("announcements.composer.scheduledAt")}
            extra={t("announcements.composer.scheduledAtHint")}
            rules={[
              { required: true, message: t("announcements.composer.scheduledAtRequired") },
              {
                validator: (_, value: Dayjs | undefined) =>
                  !value || dayjs(scheduledInstant(value)).isAfter(dayjs()) ? Promise.resolve() : Promise.reject(new Error(t("announcements.composer.scheduledAtPast"))),
              },
            ]}
          >
            <DatePicker showTime={{ format: "HH:mm", minuteStep: 5 }} format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
